import { useState, useMemo } from "react";
import { ArrowUpRight, ArrowDownLeft, Banknote, Building2, FileCheck, CreditCard } from "lucide-react";
import { cn } from "@/utils/Functions";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardPanel } from "@/components/ui/card";
import type { Transaction } from "@/types/portfolio.types";

const statusStyles: Record<string, string> = {
  completed: "text-emerald-500 bg-emerald-500/10",
  pending: "text-amber-500 bg-amber-500/10",
  scheduled: "text-blue-500 bg-blue-500/10",
};

const methodIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  wire: Banknote,
  ach: Building2,
  check: FileCheck,
  platform_escrow: CreditCard,
};

const methodLabels: Record<string, string> = {
  wire: "Wire",
  ach: "ACH",
  check: "Check",
  platform_escrow: "Escrow",
};

type Props = {
  transactions: Transaction[];
};

export function TransactionLedger({ transactions }: Props) {
  const [tab, setTab] = useState("all");

  const filtered = useMemo(() => {
    const list = transactions.filter((t) => {
      if (tab === "income") return t.direction === "in";
      if (tab === "expenses") return t.direction === "out";
      if (tab === "pending") return t.status !== "completed";
      return true;
    });
    return [...list].sort((a, b) => b.isoDate.localeCompare(a.isoDate));
  }, [transactions, tab]);

  return (
    <Card className="overflow-hidden gap-0">
      <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
        <CardTitle className="text-sm mb-0">Transactions</CardTitle>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="income">Income</TabsTrigger>
            <TabsTrigger value="expenses">Expenses</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>

      <CardPanel className="px-4 py-4">
        {filtered.map((tx) => {
          const MethodIcon = tx.method ? methodIcons[tx.method] : null;
          return (
            <div
              key={tx.id}
              className="flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-b-0 hover:bg-muted/30 transition-colors"
            >
              <div className={cn(
                "size-9 rounded-full grid place-items-center shrink-0",
                tx.direction === "in" ? "bg-brand-green/10 text-brand-green" : "bg-brand-amber/10 text-brand-amber"
              )}>
                {tx.direction === "in" ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">{tx.title}</div>
                <div className="text-xs text-muted-foreground">{tx.dealName} · {tx.counterparty}</div>
              </div>
              {MethodIcon && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-muted-foreground bg-muted/50 flex items-center gap-1">
                  <MethodIcon className="size-3" />
                  {methodLabels[tx.method!]}
                </span>
              )}
              <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full capitalize", statusStyles[tx.status])}>
                {tx.status}
              </span>
              <span className="text-xs text-muted-foreground w-24 text-right">{tx.date}</span>
              <span className={cn(
                "text-sm font-semibold tabular-nums w-24 text-right",
                tx.direction === "in" ? "text-brand-green" : "text-foreground"
              )}>
                {tx.direction === "in" ? "+" : "-"}${tx.amount.toLocaleString()}
              </span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">No transactions match this filter</div>
        )}
      </CardPanel>
    </Card>
  );
}
